import { Input, Select, Button, Dropdown, ErrorNotice, Field, MenuAction, Modal } from '@kiipu/ui';
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FolderSimple,
  Tag as TagIcon,
  PencilSimple,
  Trash,
  Plus,
  DotsThree,
  ArrowRight,
} from '@phosphor-icons/react';

import { api, type Folder, type Tag, type ItemPatch } from '../api';
import { useToast } from '../context';
import { FolderPicker, TagPicker } from './organization-picker';
import { folderDescendants, folderPath } from './organization-helpers';
import { canMutate, usePlatform } from '../lib/client-platform';
import { useDesktopDirty } from '../lib/desktop-dirty-state';

export function GroupManager({
  kind,
  onClose,
  folders,
  tags,
  onDeleted,
}: {
  kind: 'folders' | 'tags' | null;
  onClose: () => void;
  folders: Folder[];
  tags: Tag[];
  onDeleted?: (ids: string[]) => void;
}) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [parent, setParent] = useState('');
  usePlatform();
  useDesktopDirty('group-manager', Boolean(kind && (name.trim() || moving)));
  const client = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!kind) return;
      if (deleting) {
        await api.removeGroup(kind, deleting);
        return;
      }
      if (moving) {
        const folder = folders.find((entry) => entry.id === moving);
        if (!folder) throw new Error('文件夹不存在，请重新打开管理窗口。');
        await api.rename('folders', moving, folder.name, { parentId: parent || null });
        return;
      }
      if (editing) {
        await api.rename(
          kind,
          editing,
          name.trim(),
          kind === 'folders'
            ? { parentId: folders.find((entry) => entry.id === editing)?.parentId ?? null }
            : { color: tags.find((entry) => entry.id === editing)?.color ?? null },
        );
        return;
      }
      if (kind === 'folders') await api.createFolder(name.trim(), parent || null);
      else await api.createTag(name.trim());
    },
    onSuccess: () => {
      if (deleting)
        onDeleted?.(kind === 'folders' ? [...folderDescendants(deleting, folders)] : [deleting]);
      void client.invalidateQueries({ queryKey: [kind] });
      void client.invalidateQueries({ queryKey: ['items'] });
      void client.invalidateQueries({ queryKey: ['item'] });
      setName('');
      setEditing(null);
      setDeleting(null);
      setMoving(null);
      setParent('');
      toast('已更新');
    },
  });
  function reset() {
    setName('');
    setEditing(null);
    setDeleting(null);
    setMoving(null);
    setParent('');
    mutation.reset();
  }
  const entries =
    kind === 'folders'
      ? [...folders].sort((a, b) =>
          folderPath(a, folders).localeCompare(folderPath(b, folders), 'zh-CN'),
        )
      : tags;
  const movingFolder = folders.find((entry) => entry.id === moving);
  return (
    <Modal
      open={Boolean(kind)}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) {
          reset();
          onClose();
        }
      }}
      title={kind === 'folders' ? '管理文件夹' : '管理标签'}
    >
      <div className="group-manager-list">
        {entries.length ? (
          entries.map((entry) => (
            <div className="group-manager-row" key={entry.id}>
              {kind === 'folders' ? <FolderSimple size={18} /> : <TagIcon size={18} />}
              <span className="grow group-manager-path">
                {kind === 'folders' ? folderPath(entry as Folder, folders) : entry.name}
              </span>
              <Dropdown label={`管理 ${entry.name}`} icon={<DotsThree size={20} />}>
                <MenuAction
                  disabled={mutation.isPending}
                  onClick={() => {
                    reset();
                    setEditing(entry.id);
                    setName(entry.name);
                  }}
                >
                  <PencilSimple size={15} />
                  重命名
                </MenuAction>
                {kind === 'folders' ? (
                  <MenuAction
                    disabled={mutation.isPending}
                    onClick={() => {
                      reset();
                      setMoving(entry.id);
                      setParent((entry as Folder).parentId || '');
                    }}
                  >
                    <ArrowRight size={15} />
                    移动到
                  </MenuAction>
                ) : null}
                <MenuAction
                  danger
                  disabled={mutation.isPending}
                  onClick={() => {
                    reset();
                    setDeleting(entry.id);
                  }}
                >
                  <Trash size={15} />
                  删除
                </MenuAction>
              </Dropdown>
            </div>
          ))
        ) : (
          <p className="muted manager-empty">
            还没有{kind === 'folders' ? '文件夹' : '标签'}，创建第一个。
          </p>
        )}
      </div>
      {deleting ? (
        <div className="stack">
          <p className="muted">
            确认删除“{entries.find((entry) => entry.id === deleting)?.name}”？
            {kind === 'folders'
              ? '它的子文件夹也会删除，其中的内容会移到“未整理”，正文、备注与标签会保留。'
              : '此标签会从所有内容中移除，内容本身、文件夹和备注会保留。'}
          </p>
          <ErrorNotice error={mutation.error} />
          <div className="modal-actions">
            <Button disabled={mutation.isPending} onClick={reset}>
              取消
            </Button>
            <Button variant="danger" busy={mutation.isPending} onClick={() => mutation.mutate()}>
              删除
            </Button>
          </div>
        </div>
      ) : moving ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (!mutation.isPending) mutation.mutate();
          }}
        >
          <p className="muted">
            移动“{movingFolder ? folderPath(movingFolder, folders) : '文件夹'}
            ”及其子文件夹，内容与标签会保留。
          </p>
          <FolderPicker
            key={moving}
            folders={folders}
            value={parent}
            onChange={(value) => {
              setParent(value);
              mutation.reset();
            }}
            label="移动到"
            rootLabel="顶层文件夹"
            excludeIds={folderDescendants(moving, folders)}
            allowCreate={false}
            disabled={mutation.isPending}
          />
          <p className="field-hint">文件夹最多支持五层，无法选择自身或子文件夹。</p>
          <ErrorNotice error={mutation.error} />
          <div className="modal-actions">
            <Button disabled={mutation.isPending} onClick={reset}>
              取消
            </Button>
            <Button
              variant="primary"
              type="submit"
              busy={mutation.isPending}
              disabled={parent === (movingFolder?.parentId || '')}
            >
              移动文件夹
            </Button>
          </div>
        </form>
      ) : (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() && !mutation.isPending) mutation.mutate();
          }}
        >
          <Field label={editing ? '重命名' : kind === 'folders' ? '新建文件夹' : '新建标签'}>
            <Input
              value={name}
              disabled={mutation.isPending}
              onChange={(event) => {
                setName(event.target.value);
                mutation.reset();
              }}
              placeholder={kind === 'folders' ? '例如：设计灵感' : '例如：待研究'}
              maxLength={kind === 'folders' ? 100 : 50}
              required
            />
          </Field>
          {kind === 'folders' && !editing && folders.length ? (
            <FolderPicker
              folders={folders}
              value={parent}
              onChange={(value) => {
                setParent(value);
                mutation.reset();
              }}
              label="上级文件夹"
              rootLabel="顶层文件夹"
              allowCreate={false}
              disabled={mutation.isPending}
            />
          ) : null}
          <ErrorNotice error={mutation.error} />
          <div className="modal-actions">
            {editing ? (
              <Button disabled={mutation.isPending} onClick={reset}>
                取消
              </Button>
            ) : null}
            <Button
              variant="primary"
              type="submit"
              busy={mutation.isPending}
              disabled={!name.trim()}
            >
              {editing ? (
                '保存'
              ) : (
                <>
                  <Plus size={16} />
                  创建
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function OrganizeDialog({
  ids,
  initialFolderId,
  initialTagIds = [],
  folders,
  tags,
  onClose,
  onSaved,
}: {
  ids: string[];
  initialFolderId?: string | null;
  initialTagIds?: string[];
  folders: Folder[];
  tags: Tag[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const multiple = ids.length > 1;
  usePlatform();
  const [folder, setFolder] = useState(multiple ? '__keep' : initialFolderId || '');
  const [selectedTags, setSelectedTags] = useState(multiple ? [] : initialTagIds);
  const [tagMode, setTagMode] = useState<'keep' | 'add' | 'remove' | 'replace'>(
    multiple ? 'keep' : 'replace',
  );
  const [folderBusy, setFolderBusy] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  useDesktopDirty(
    'organize',
    multiple
      ? folder !== '__keep' || tagMode !== 'keep'
      : folder !== (initialFolderId || '') ||
          JSON.stringify(selectedTags) !== JSON.stringify(initialTagIds),
  );
  const mutationId = useRef(crypto.randomUUID());
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const patch: ItemPatch = {
        ...(folder === '__keep' ? {} : { folderId: folder || null }),
        ...(!multiple ? { tagIds: selectedTags, mutationId: mutationId.current } : {}),
      };
      if (!multiple) await api.patch(ids[0]!, patch);
      else
        await api.batch(ids, patch, {
          ...(tagMode !== 'keep' ? { tagOperation: { mode: tagMode, tagIds: selectedTags } } : {}),
          mutationId: mutationId.current,
        });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['items'] });
      void client.invalidateQueries({ queryKey: ['item'] });
      onSaved();
      onClose();
    },
  });
  function changed() {
    mutationId.current = crypto.randomUUID();
    mutation.reset();
  }
  const disabled = mutation.isPending || folderBusy || tagBusy || !canMutate();
  const noChanges =
    multiple &&
    folder === '__keep' &&
    (tagMode === 'keep' || (tagMode !== 'replace' && !selectedTags.length));
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !disabled) onClose();
      }}
      title={multiple ? `整理 ${ids.length} 条内容` : '整理内容'}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled && !noChanges) mutation.mutate();
        }}
      >
        <FolderPicker
          folders={folders}
          value={folder}
          onChange={(value) => {
            setFolder(value);
            changed();
          }}
          keepLabel={multiple ? '保留各自的文件夹' : undefined}
          disabled={mutation.isPending || tagBusy}
          onBusyChange={setFolderBusy}
        />
        {multiple ? (
          <Field label="标签操作">
            <Select
              value={tagMode}
              disabled={disabled}
              onChange={(event) => {
                setTagMode(event.target.value as typeof tagMode);
                setSelectedTags([]);
                changed();
              }}
            >
              <option value="keep">保留各自的标签</option>
              <option value="add">添加标签</option>
              <option value="remove">移除标签</option>
              <option value="replace">替换全部标签</option>
            </Select>
          </Field>
        ) : null}
        {tagMode !== 'keep' ? (
          <TagPicker
            tags={tags}
            value={selectedTags}
            onChange={(value) => {
              setSelectedTags(value);
              changed();
            }}
            allowCreate={tagMode !== 'remove'}
            disabled={mutation.isPending || folderBusy}
            onBusyChange={setTagBusy}
          />
        ) : null}
        {multiple && tagMode === 'replace' ? (
          <p className="organization-warning" role="note">
            将用所选标签覆盖这 {ids.length} 条内容的全部原有标签。
            {!selectedTags.length ? '未选择标签时，将清空它们的标签。' : ''}
          </p>
        ) : multiple && tagMode === 'add' ? (
          <p className="field-hint">只添加所选标签，每条内容原有的标签都会保留。</p>
        ) : multiple && tagMode === 'remove' ? (
          <p className="field-hint">只移除所选标签，其他标签都会保留。</p>
        ) : null}
        <ErrorNotice error={mutation.error} />
        <div className="modal-actions">
          <Button onClick={onClose} disabled={disabled}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            busy={mutation.isPending}
            disabled={disabled || noChanges}
          >
            {multiple ? '应用整理' : '保存'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
